import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase/client';
import { User, LogIn, Mail, Lock, MapPin, Building, UserPlus, Phone, CheckSquare, Square, Eye, EyeOff, CheckCircle, XCircle, Calculator, Clock, Printer, Shell, BarChart4, Shield, Coins } from 'lucide-react';
import { initializeUserTokens } from '../services/tokenService';
import { analyticsService } from '../services/analyticsService';
import ForgotPasswordForm from './ForgotPasswordForm';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import TruckLogo from './TruckLogo';

interface AuthFormProps {
  onSuccess: () => void;
}

// Define different authentication states
type AuthState = 'login' | 'register' | 'forgotPassword';

// Function to check if email already exists
const checkEmailExists = async (email: string): Promise<boolean> => {
  try {
    // Try to sign in with the email and a dummy password
    // If the user exists, we'll get a specific error about invalid credentials
    // If the user doesn't exist, we'll get a different error
    const { error } = await supabase.auth.signInWithPassword({
      email: email,
      password: 'dummy-password-for-email-check'
    });
    
    if (error) {
      // Check the specific error messages that indicate the user exists
      if (error.message.includes('Invalid login credentials') || 
          error.message.includes('Email not confirmed') ||
          error.message.includes('Invalid email or password')) {
        return true; // User exists
      }
      // Other errors (like "User not found") indicate the user doesn't exist
      return false;
    }
    
    // If no error (unlikely with dummy password), user exists
    return true;
  } catch (error) {
    console.error('Error checking if email exists:', error);
    return false; // On error, assume email doesn't exist to allow registration attempt
  }
};

const countryCodes = [
  // North America
  { code: '+52', country: 'México' },
  { code: '+1', country: 'Estados Unidos/Canadá' },
  { code: '+1', country: 'Puerto Rico' },
  { code: '+1', country: 'Jamaica' },
  
  // Central America
  { code: '+502', country: 'Guatemala' },
  { code: '+503', country: 'El Salvador' },
  { code: '+504', country: 'Honduras' },
  { code: '+505', country: 'Nicaragua' },
  { code: '+506', country: 'Costa Rica' },
  { code: '+507', country: 'Panamá' },
  { code: '+501', country: 'Belice' },
  
  // South America
  { code: '+57', country: 'Colombia' },
  { code: '+56', country: 'Chile' },
  { code: '+54', country: 'Argentina' },
  { code: '+51', country: 'Perú' },
  { code: '+58', country: 'Venezuela' },
  { code: '+55', country: 'Brasil' },
  { code: '+593', country: 'Ecuador' },
  { code: '+591', country: 'Bolivia' },
  { code: '+595', country: 'Paraguay' },
  { code: '+598', country: 'Uruguay' },
  { code: '+592', country: 'Guyana' },
  { code: '+597', country: 'Surinam' },
  
  // Europe
  { code: '+34', country: 'España' },
  { code: '+39', country: 'Italia' },
  { code: '+33', country: 'Francia' },
  { code: '+49', country: 'Alemania' },
  { code: '+44', country: 'Reino Unido' },
  { code: '+351', country: 'Portugal' },
  { code: '+31', country: 'Países Bajos' },
  { code: '+32', country: 'Bélgica' },
  { code: '+41', country: 'Suiza' },
  { code: '+43', country: 'Austria' },
  { code: '+46', country: 'Suecia' },
  { code: '+47', country: 'Noruega' },
  { code: '+45', country: 'Dinamarca' },
  { code: '+358', country: 'Finlandia' },
  { code: '+48', country: 'Polonia' },
  { code: '+420', country: 'República Checa' },
  { code: '+30', country: 'Grecia' },
  { code: '+36', country: 'Hungría' },
  { code: '+353', country: 'Irlanda' },
  { code: '+40', country: 'Rumania' },
  { code: '+421', country: 'Eslovaquia' },
  { code: '+386', country: 'Eslovenia' },
  { code: '+385', country: 'Croacia' },
  { code: '+359', country: 'Bulgaria' },
  
  // Asia
  { code: '+81', country: 'Japón' },
  { code: '+82', country: 'Corea del Sur' },
  { code: '+86', country: 'China' },
  { code: '+91', country: 'India' },
  { code: '+65', country: 'Singapur' },
  { code: '+66', country: 'Tailandia' },
  { code: '+60', country: 'Malasia' },
  { code: '+62', country: 'Indonesia' },
  { code: '+63', country: 'Filipinas' },
  { code: '+84', country: 'Vietnam' },
  { code: '+852', country: 'Hong Kong' },
  { code: '+886', country: 'Taiwán' },
  { code: '+92', country: 'Pakistán' },
  { code: '+971', country: 'Emiratos Árabes Unidos' },
  { code: '+966', country: 'Arabia Saudita' },
  { code: '+972', country: 'Israel' },
  { code: '+90', country: 'Turquía' },
  
  // Africa
  { code: '+27', country: 'Sudáfrica' },
  { code: '+20', country: 'Egipto' },
  { code: '+212', country: 'Marruecos' },
  { code: '+234', country: 'Nigeria' },
  { code: '+254', country: 'Kenia' },
  { code: '+233', country: 'Ghana' },
  { code: '+216', country: 'Túnez' },
  { code: '+213', country: 'Argelia' },
  
  // Oceania
  { code: '+61', country: 'Australia' },
  { code: '+64', country: 'Nueva Zelanda' },
  { code: '+675', country: 'Papúa Nueva Guinea' },
  { code: '+679', country: 'Fiyi' }
];

const AuthForm: React.FC<AuthFormProps> = ({ onSuccess }) => {
  const [authState, setAuthState] = useState<AuthState>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [company, setCompany] = useState('');
  const [countryCode, setCountryCode] = useState('+52');
  const [phone, setPhone] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showMobileBenefits, setShowMobileBenefits] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const { t } = useLanguage();

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    number: false
  });

  // Check password strength as user types
  useEffect(() => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password)
    });
  }, [password]);

  useEffect(() => {
    // Track page view for login/register page
    analyticsService.trackPageView('auth_page');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate terms acceptance
    if (authState === 'register' && !acceptTerms) {
      setError(t('auth.agreedToTerms'));
      return;
    }
    
    // Validate password requirements for registration
    if (authState === 'register') {
      // Validate password strength
      if (!passwordStrength.length || !passwordStrength.uppercase || !passwordStrength.number) {
        setError(t('errors.passwordRequirements'));
        return;
      }
      
      // Validate password matching
      if (password !== confirmPassword) {
        setError(t('errors.passwordsMismatch'));
        return;
      }
    }
    
    setLoading(true);

    try {
      if (authState === 'login') {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        
        if (error) throw error;
        
        // Track successful login
        await analyticsService.trackSignIn('email');
        onSuccess();
      } else if (authState === 'register') {
        // Validar campos adicionales para registro
        if (!name.trim()) {
          throw new Error(t('errors.nameRequired'));
        }
        if (!city.trim()) {
          throw new Error(t('errors.cityRequired'));
        }
        if (!postalCode.trim()) {
          throw new Error(t('errors.postalCodeRequired'));
        }
        if (!phone.trim()) {
          throw new Error(t('errors.phoneRequired'));
        }

        // Crear usuario
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              display_name: name,
              phone: (countryCode + ' ' + phone).trim(),
              city: city.trim(),
              postal_code: postalCode.trim(),
              company: company.trim(),
              accepted_terms: true
            }
          }
        });
        
        if (error) throw error;
        if (!data.user) throw new Error('No se pudo crear el usuario');
        
        // Show email confirmation message instead of proceeding
        setRegisteredEmail(email);
        setShowEmailConfirmation(true);
        
        // Track successful registration
        await analyticsService.trackSignUp('email');
        
        // Don't call onSuccess() here - user needs to confirm email first
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || t('errors.authError'));
      
      // Track authentication error
      analyticsService.trackCustomEvent('auth_error', {
        error_message: err.message || 'Unknown auth error',
        auth_state: authState
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setAuthState('forgotPassword');
    
    // Track forgot password click
    analyticsService.trackCustomEvent('forgot_password_click');
  };

  const handleBackToLogin = () => {
    setShowEmailConfirmation(false);
    setAuthState('login');
    setError(null);
    setRegisteredEmail('');
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent checkbox toggle when clicking on the terms link
  };
  
  // Password validation helper
  const isPasswordValid = (): boolean => {
    return passwordStrength.length && passwordStrength.uppercase && passwordStrength.number;
  };

  // Show email confirmation screen
  if (showEmailConfirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
          style={{ 
            backgroundImage: 'url("https://images.unsplash.com/photo-1726776230751-183496c51f00?q=80&w=1510&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D")',
            backgroundSize: 'cover'
          }}
        >
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>

        <div className="w-full max-w-md z-10 px-4">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="relative h-40 sm:h-48 bg-gradient-to-r from-primary-light to-primary-dark">
              <div className="absolute inset-0 bg-black opacity-30"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <TruckLogo size={100} className="text-white" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
                <h1 className="text-2xl sm:text-3xl font-bold uppercase text-center">
                  COTIZATRIN
                </h1>
              </div>
              <div className="absolute top-0 right-0 p-3">
                <LanguageSwitcher variant="icon" className="text-white hover:bg-white/20" />
              </div>
            </div>

            <div className="p-6">
              <div className="text-center">
                <div className="bg-blue-100 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <Mail className="h-12 w-12 text-blue-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('auth.emailConfirmation.title')}</h2>
                <p className="text-gray-600 mb-6">
                  {t('auth.emailConfirmation.subtitle')}
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold text-blue-800 mb-3">{t('auth.emailConfirmation.instructionsTitle')}</h3>
                  <div className="text-left space-y-3">
                    <div className="flex items-start">
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5 text-sm">
                        1
                      </div>
                      <p className="text-blue-700">
                        <strong>{t('auth.emailConfirmation.step1Title')}</strong> {t('auth.emailConfirmation.step1Description')} <strong>{registeredEmail}</strong>
                      </p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5 text-sm">
                        2
                      </div>
                      <p className="text-blue-700">
                        <strong>{t('auth.emailConfirmation.step2Title')}</strong> {t('auth.emailConfirmation.step2Description')}
                      </p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5 text-sm">
                        3
                      </div>
                      <p className="text-blue-700">
                        <strong>{t('auth.emailConfirmation.step3Title')}</strong> {t('auth.emailConfirmation.step3Description')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center text-green-800">
                    <Coins className="h-5 w-5 mr-2" />
                    <span className="font-semibold">{t('auth.emailConfirmation.freeCreditsMessage')}</span>
                  </div>
                </div>

                <button
                  onClick={handleBackToLogin}
                  className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-md transition duration-200 flex items-center justify-center"
                >
                  <LogIn size={18} className="mr-2" />
                  {t('auth.emailConfirmation.backToLogin')}
                </button>

                <div className="mt-4 text-center text-sm text-gray-600">
                  <p>{t('auth.emailConfirmation.noEmailReceived')}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 text-center text-white text-sm">
            © 2025 Cotizatrin. {t('common.copyright')}
          </div>
        </div>
      </div>
    );
  }

  // Render different states
  if (authState === 'forgotPassword') {
    return (
      <ForgotPasswordForm 
        onBackToLogin={() => {
          setAuthState('login');
          setError(null);
        }} 
      />
    );
  }

  // Main login/register form
  const isLogin = authState === 'login';
  
  // Benefit item component
  const BenefitItem = ({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) => (
    <div className="mb-4 last:mb-0">
      <div className="flex items-start">
        <div className="p-2 bg-primary-light rounded-lg mr-3 flex-shrink-0">
          {icon}
        </div>
        <div>
          <h3 className="font-medium text-gray-800 text-sm">{title}</h3>
          <p className="text-xs text-gray-600 mt-1">{description}</p>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen flex items-center justify-center relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
        style={{ 
          backgroundImage: 'url("https://images.unsplash.com/photo-1726776230751-183496c51f00?q=80&w=1510&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D")',
          backgroundSize: 'cover'
        }}
      >
        {/* Overlay to darken the image and improve text readability */}
        <div className="absolute inset-0 bg-black opacity-60"></div>
      </div>

      {/* Main content container - removed max-width to allow benefits panel to reach edge */}
      <div className="w-full px-0 z-10 flex justify-center">
        <div className="flex flex-row w-full">
          {/* Central content container with the login form - fixed width for consistency */}
          <div className="mx-auto w-full max-w-md px-4">
            {/* Login/Register Form Card */}
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="relative h-40 sm:h-48 bg-gradient-to-r from-primary-light to-primary-dark">
                <div className="absolute inset-0 bg-black opacity-30"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <TruckLogo size={100} className="text-white" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
                  <h1 className="text-2xl sm:text-3xl font-bold uppercase text-center">
                    COTIZATRIN
                  </h1>
                </div>
                <div className="absolute top-0 right-0 p-3">
                  <LanguageSwitcher variant="icon" className="text-white hover:bg-white/20" />
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                  {/* Removed the title text */}
                  
                  {/* Pestañas para cambiar entre login y registro */}
                  <div className="flex rounded-md overflow-hidden border border-gray-200 mx-auto">
                    <button
                      onClick={() => setAuthState('login')}
                      className={`px-3 py-1 text-sm ${
                        isLogin 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {t('auth.login')}
                    </button>
                    <button
                      onClick={() => setAuthState('register')}
                      className={`px-3 py-1 text-sm ${
                        !isLogin 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {t('auth.register')}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {/* Mobile Benefits Toggle */}
                <div className="md:hidden mb-4">
                  <button 
                    onClick={() => setShowMobileBenefits(!showMobileBenefits)}
                    className="w-full flex justify-between items-center p-3 bg-primary/10 rounded-lg text-primary"
                  >
                    <span className="font-medium">{t('auth.benefits.title')}</span>
                  </button>
                  
                  {/* Mobile Benefits Panel */}
                  {showMobileBenefits && (
                    <div className="mt-2 p-4 bg-white border border-gray-200 rounded-lg shadow-md">
                      <div className="space-y-3">
                        <BenefitItem 
                          icon={<Calculator size={18} className="text-white" />} 
                          title={t('auth.benefits.accurate.title')}
                          description={t('auth.benefits.accurate.description')}
                        />
                        
                        <BenefitItem 
                          icon={<Clock size={18} className="text-white" />} 
                          title={t('auth.benefits.time.title')}
                          description={t('auth.benefits.time.description')}
                        />
                        
                        <BenefitItem 
                          icon={<Printer size={18} className="text-white" />} 
                          title={t('auth.benefits.professional.title')}
                          description={t('auth.benefits.professional.description')}
                        />
                      </div>
                      
                      {/* Mini trust indicators */}
                      <div className="mt-4 pt-3 border-t border-gray-200 flex justify-center gap-8">
                        <div className="text-center">
                          <div className="font-bold text-primary text-lg">5,000+</div>
                          <div className="text-xs text-gray-600">{t('auth.benefits.quotationsGenerated')}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <form onSubmit={handleSubmit}>
                  {!isLogin && (
                    <div className="mb-4">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('auth.fullName')} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User size={16} className="text-gray-400" />
                        </div>
                        <input
                          id="name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('auth.email')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail size={16} className="text-gray-400" />
                      </div>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('auth.password')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock size={16} className="text-gray-400" />
                      </div>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={16} className="text-gray-400" />
                        ) : (
                          <Eye size={16} className="text-gray-400" />
                        )}
                      </button>
                    </div>
                    
                    {isLogin && (
                      <div className="mt-1 text-right">
                        <button
                          type="button"
                          onClick={handleForgotPasswordClick}
                          className="text-xs text-primary hover:text-primary-dark"
                        >
                          {t('auth.forgotPassword')}
                        </button>
                      </div>
                    )}
                    
                    {!isLogin && (
                      <div className="mt-2 grid grid-cols-1 gap-1">
                        <div className="flex items-center text-xs">
                          {passwordStrength.length ? 
                            <CheckCircle size={14} className="text-green-500 mr-1 flex-shrink-0" /> : 
                            <XCircle size={14} className="text-red-500 mr-1 flex-shrink-0" />
                          }
                          <span className={passwordStrength.length ? "text-green-600" : "text-gray-500"}>
                            {t('auth.passwordRequirements.length')}
                          </span>
                        </div>
                        <div className="flex items-center text-xs">
                          {passwordStrength.uppercase ? 
                            <CheckCircle size={14} className="text-green-500 mr-1 flex-shrink-0" /> : 
                            <XCircle size={14} className="text-red-500 mr-1 flex-shrink-0" />
                          }
                          <span className={passwordStrength.uppercase ? "text-green-600" : "text-gray-500"}>
                            {t('auth.passwordRequirements.uppercase')}
                          </span>
                        </div>
                        <div className="flex items-center text-xs">
                          {passwordStrength.number ? 
                            <CheckCircle size={14} className="text-green-500 mr-1 flex-shrink-0" /> : 
                            <XCircle size={14} className="text-red-500 mr-1 flex-shrink-0" />
                          }
                          <span className={passwordStrength.number ? "text-green-600" : "text-gray-500"}>
                            {t('auth.passwordRequirements.number')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {!isLogin && (
                    <div className="mb-4">
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('auth.confirmPassword')} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock size={16} className="text-gray-400" />
                        </div>
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                            confirmPassword && password !== confirmPassword ? 'border-red-300' : 'border-gray-300'
                          }`}
                          required
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff size={16} className="text-gray-400" />
                          ) : (
                            <Eye size={16} className="text-gray-400" />
                          )}
                        </button>
                      </div>
                      {confirmPassword && password !== confirmPassword && (
                        <p className="mt-1 text-xs text-red-500">{t('errors.passwordsMismatch')}</p>
                      )}
                    </div>
                  )}

                  {!isLogin && (
                    <>
                      <div className="mb-4">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('auth.phoneNumber')} <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="sm:w-1/3">
                            <select
                              id="countryCode"
                              value={countryCode}
                              onChange={(e) => setCountryCode(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            >
                              {countryCodes.map((country) => (
                                <option key={`${country.code}-${country.country}`} value={country.code}>
                                  {country.code} ({country.country})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="sm:w-2/3 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Phone size={16} className="text-gray-400" />
                            </div>
                            <input
                              id="phone"
                              type="tel"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              placeholder={t('auth.phoneNoSpaces')}
                              required
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('auth.city')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MapPin size={16} className="text-gray-400" />
                          </div>
                          <input
                            id="city"
                            type="text"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            required
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('auth.postalCode')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="postalCode"
                          type="text"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div className="mb-6">
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('auth.company')}
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Building size={16} className="text-gray-400" />
                          </div>
                          <input
                            id="company"
                            type="text"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>

                      {/* Checkbox de aceptación de términos */}
                      <div className="mb-6">
                        <div 
                          className="flex items-start cursor-pointer" 
                          onClick={() => setAcceptTerms(!acceptTerms)}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {acceptTerms ? (
                              <CheckSquare size={18} className="text-primary" />
                            ) : (
                              <Square size={18} className="text-gray-400" />
                            )}
                          </div>
                          <div className="ml-2 text-sm">
                            <p className="text-gray-700">
                              {t('auth.agreedToTerms')}{' '}
                              <a
                                href="/terminos-y-condiciones"
                                onClick={(e) => {
                                  handleTermsClick(e);
                                  e.preventDefault();
                                  window.location.hash = 'terminos-y-condiciones';
                                }}
                                className="text-primary hover:text-primary-dark font-medium underline"
                              >
                                {t('auth.termsAndConditions')}
                              </a>{' '}
                              {t('common.and')}{' '}
                              <a
                                href="/aviso-de-privacidad"
                                onClick={(e) => {
                                  handleTermsClick(e);
                                  e.preventDefault();
                                  window.location.hash = 'aviso-de-privacidad';
                                }}
                                className="text-primary hover:text-primary-dark font-medium underline"
                              >
                                {t('auth.privacyPolicy')}
                              </a>
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {isLogin && (
                    <div className="mb-6">
                      {/* Checkbox de recordar sesión para inicio de sesión (opcional) */}
                      <div 
                        className="flex items-start cursor-pointer mt-4" 
                        onClick={() => setAcceptTerms(!acceptTerms)}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {acceptTerms ? (
                            <CheckSquare size={18} className="text-primary" />
                          ) : (
                            <Square size={18} className="text-gray-400" />
                          )}
                        </div>
                        <div className="ml-2 text-sm">
                          <p className="text-gray-700">
                            {t('auth.rememberMe')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || (!isLogin && (!isPasswordValid() || password !== confirmPassword))}
                    className={`w-full ${
                      isLogin 
                        ? 'bg-primary hover:bg-primary-dark' 
                        : 'bg-green-600 hover:bg-green-700'
                    } text-white font-semibold py-4 px-6 text-lg rounded-md transition duration-200 flex items-center justify-center ${
                      loading || (!isLogin && (!isPasswordValid() || password !== confirmPassword)) 
                        ? 'opacity-60 cursor-not-allowed' 
                        : ''
                    }`}
                  >
                    {loading ? (
                      <span className="animate-pulse">{t('common.loading')}</span>
                    ) : isLogin ? (
                      <>
                        <LogIn size={22} className="mr-2" />
                        {t('auth.login')}
                      </>
                    ) : (
                      <>
                        <UserPlus size={22} className="mr-2" />
                        {t('auth.register')}
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => setAuthState(isLogin ? 'register' : 'login')}
                    className="text-sm text-primary hover:text-primary-dark"
                  >
                    {isLogin
                      ? t('auth.noAccount')
                      : t('auth.haveAccount')}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mt-4 text-center text-white text-sm">
              © 2025 Cotizatrin. {t('common.copyright')}
              <div className="mt-2 text-xs text-gray-300">
                <p>Tallinn, Estonia</p>
                <p className="mt-1">
                  <a href="mailto:info_cotizatrin@autotrin.com" className="hover:text-white transition-colors">
                    info_cotizatrin@autotrin.com
                  </a>
                </p>
              </div>
              <div className="mt-3 flex justify-center">
                <LanguageSwitcher variant="footer" className="bg-white/20 hover:bg-white/30" />
              </div>
              <div className="mt-1 flex flex-wrap justify-center gap-3">
                <a
                  href="/terminos-y-condiciones"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.hash = 'terminos-y-condiciones';
                  }}
                  className="text-xs text-gray-300 hover:text-white underline"
                >
                  {t('legal.termsConditions')}
                </a>
                <span className="text-gray-500 hidden sm:inline">|</span>
                <a
                  href="/aviso-de-privacidad"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.hash = 'aviso-de-privacidad';
                  }}
                  className="text-xs text-gray-300 hover:text-white underline"
                >
                  {t('legal.privacyPolicy')}
                </a>
              </div>
            </div>
          </div>

          {/* Desktop Benefits Panel - Now positioned at the rightmost edge */}
          <div className="hidden md:block w-72 bg-white rounded-lg shadow-xl overflow-hidden h-fit m-0 mr-0 self-center">
            <div className="h-40 bg-gradient-to-r from-primary to-primary-dark relative">
              <div className="absolute inset-0 bg-black opacity-30"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <h2 className="text-white text-xl font-bold">{t('auth.benefits.title')}</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <BenefitItem 
                  icon={<Calculator size={20} className="text-white" />} 
                  title={t('auth.benefits.accurate.title')}
                  description={t('auth.benefits.accurate.description')}
                />
                
                <BenefitItem 
                  icon={<Clock size={20} className="text-white" />} 
                  title={t('auth.benefits.time.title')}
                  description={t('auth.benefits.time.description')}
                />
                
                <BenefitItem 
                  icon={<Printer size={20} className="text-white" />} 
                  title={t('auth.benefits.professional.title')}
                  description={t('auth.benefits.professional.description')}
                />
                
                <BenefitItem 
                  icon={<Shell size={20} className="text-white" />} 
                  title={t('auth.benefits.tollData.title')}
                  description={t('auth.benefits.tollData.description')}
                />
                
                <BenefitItem 
                  icon={<BarChart4 size={20} className="text-white" />} 
                  title={t('auth.benefits.multiCurrency.title')}
                  description={t('auth.benefits.multiCurrency.description')}
                />
                
                <BenefitItem 
                  icon={<Shield size={20} className="text-white" />} 
                  title={t('auth.benefits.security.title')}
                  description={t('auth.benefits.security.description')}
                />
              </div>

              {/* Trust indicators */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex justify-center">
                  <div className="text-center">
                    <div className="font-bold text-primary text-2xl">5,000+</div>
                    <div className="text-xs text-gray-600">{t('auth.benefits.quotationsGenerated')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;